import '../App.css';
import { useEffect, useState } from 'react';
import { WebApp } from '../utils/webAppShim';
import OTPDisplay from './OTPDisplay';
import RegisterDevice from './RegisterDevice';
import RegisterForm from './RegisterForm';
import NfcButton from './NfcButton';
import LPAdminPanel from './LPAdminPanel';
import LockScreen from './LockScreen';
import Settings from './Settings';
import { useUser } from '../context/UserContext';


const Home = () => {
    // State for different UI screens
    const [otpDisplayMode, setOtpDisplayMode] = useState(null);
    const [requestedScreen, setRequestedScreen] = useState(null);
    const [location, setLocation] = useState(null);
    const [loginSuccess, setLoginSuccess] = useState(false);
    const [showRegistration, setShowRegistration] = useState(false);
    const [showNfc, setShowNfc] = useState(false);
    const [showLPAdmin, setShowLPAdmin] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [isPrivacyShieldVisible, setIsPrivacyShieldVisible] = useState(false);
    // We store the scanned data in case the device needs registration.
    const [qrData, setQrData] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [deviceId, setDeviceId] = useState(null);
    const { user, loading, accessDenied, registrationAllowed, blockReason, isLocked, unlockApp } = useUser();

    const PERMISSION_ADMIN = 1;
    const PERMISSION_OTP = 524288;
    const adminUrl = process.env.REACT_APP_ADMIN_URL;

    const isLPUser = !!(user && user.legalPerson && !user.isSuperAdmin);
    const isLPAdmin = !!(user && user.isLegalPersonAdmin && !user.isSuperAdmin);
    const permissionsNum = (user && typeof user.permissions === "number") ? user.permissions : (Number(user?.permissions) >>> 0) || 0;
    const isGlobalAdmin = !!(user && (((permissionsNum & PERMISSION_ADMIN) === PERMISSION_ADMIN) || user.isSuperAdmin));
    const apiUrl = process.env.REACT_APP_API_URL;

    const withApiPrefix = (baseUrl) => {
        const normalized = String(baseUrl || '').replace(/\/+$/, '');
        if (normalized.endsWith('/api')) return normalized;
        return `${normalized}/api`;
    };

    const fetchWithApiFallback = async (baseUrl, path, options) => {
        const primary = `${String(baseUrl || '').replace(/\/+$/, '')}${path}`;
        let response = await fetch(primary, options);
        if (response.status !== 404) return response;

        const fallbackBase = withApiPrefix(baseUrl);
        const fallback = `${fallbackBase}${path}`;
        if (fallback === primary) return response;
        return fetch(fallback, options);
    };

    const goToAdmin = () => {
        if (adminUrl) window.location.href = adminUrl;
    };

    const handleSupportClick = () => {
        window.open("https://app.leadteh.ru/w/djk3S", "_blank", "noopener,noreferrer");
    };

    const handleSupportLeadtehClick = () => {
        window.open("https://app.leadteh.ru/w/djk3S", "_blank", "noopener,noreferrer");
    };


    useEffect(() => {
        WebApp.ready();
        WebApp.expand();
    }, []);

    useEffect(() => {
        if (typeof document === "undefined") return undefined;

        const updateShield = () => {
            setIsPrivacyShieldVisible(document.visibilityState !== "visible" || !document.hasFocus());
        };
        const blockCopyActions = (event) => event.preventDefault();

        document.addEventListener("visibilitychange", updateShield);
        window.addEventListener("blur", updateShield);
        window.addEventListener("focus", updateShield);
        document.addEventListener("copy", blockCopyActions);
        document.addEventListener("cut", blockCopyActions);
        document.addEventListener("contextmenu", blockCopyActions);
        document.addEventListener("selectstart", blockCopyActions);
        document.addEventListener("dragstart", blockCopyActions);

        updateShield();

        return () => {
            document.removeEventListener("visibilitychange", updateShield);
            window.removeEventListener("blur", updateShield);
            window.removeEventListener("focus", updateShield);
            document.removeEventListener("copy", blockCopyActions);
            document.removeEventListener("cut", blockCopyActions);
            document.removeEventListener("contextmenu", blockCopyActions);
            document.removeEventListener("selectstart", blockCopyActions);
            document.removeEventListener("dragstart", blockCopyActions);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search || '');
        const screen = (params.get('screen') || '').trim();
        setRequestedScreen(screen || null);
    }, []);

    useEffect(() => {
        if (!requestedScreen) return;
        if (!user) return;
        if (otpDisplayMode) return;

        const hasUserOtpPermission = !!user;
        if (requestedScreen === 'otp' && hasUserOtpPermission) {
            setOtpDisplayMode('user');
            return;
        }

        if ((requestedScreen === 'adminOtp' || requestedScreen === 'otpAdmin') && isGlobalAdmin) {
            setOtpDisplayMode('admin');
        }
    }, [requestedScreen, user, otpDisplayMode, isGlobalAdmin]);


    if (loading) {
        return <div className="container">
            <div className="flex justify-center items-center min-h-screen text-white">Загрузка...</div>
        </div>;
    }

    if (accessDenied) {
        return (
            <div className="flex flex-col justify-center items-center min-h-screen bg-red-500 text-white p-4 rounded-lg">
                <h2 className="text-2xl font-semibold">Отказано в доступе</h2>
                {blockReason && <p className="text-lg text-center mt-2">{blockReason}</p>}
                <button
                    onClick={handleSupportLeadtehClick}
                    className="w-full max-w-md bg-blue-500 py-2 mt-5 rounded-md hover:bg-blue-600 transition-colors"
                >
                    Техподдержка
                </button>
            </div>
        );
    }

    if (registrationAllowed) {
        return <RegisterForm apiUrl={apiUrl} onSuccess={() => window.location.reload()} />;
    }


    const handleNFC = async () => {
        if (!location) {
            if (!WebApp.LocationManager.isInited) {
                await new Promise((resolve) => {
                    WebApp.LocationManager.init(() => {
                        if (!WebApp.LocationManager.isInited) {
                            WebApp.showAlert("Невозможно получить локацию");
                            resolve(false);
                            return;
                        }
                        resolve(true);
                    });
                });
            }
            const newLocation = await getCurrentLocation();
            if (!newLocation) {
                return;
            }
            setLocation(newLocation);
        }

        const queryParameters = {
            sessionID: sessionId,
            deviceId: deviceId,
            userId: user._id,
            location: location,
        };

        const urlParams = new URLSearchParams(queryParameters).toString();

        console.log(`User agent is: ${navigator.userAgent}`);
        console.log(`is Iphone: ${navigator.userAgent.includes("iPhone")}`);

        const siteOrigin = window.location.origin;

        if (navigator.userAgent.includes("iPhone")) {
            WebApp.openLink(`${siteOrigin}/redirect?link=${encodeURIComponent(`nfcscannerapp://?${urlParams}`)}`, { try_browser: 'chrome', try_instant_view: false });
        } else {
            WebApp.openLink(`${siteOrigin}/nfc-scan?${urlParams}`, {
                try_browser: 'chrome',
                try_instant_view: false,
            });
        }
    }

    const handleLocationError = (error) => {
        console.error(error);
        WebApp.showAlert(`Location Error: ${error}`);
    };

    const checkLocationAccess = async () => {
        return new Promise((resolve) => {
            WebApp.LocationManager.getLocation((locationData) => {
                if (!locationData) {
                    // WebApp.showAlert("Доступ к геолокации отключен. Включите его в настройках Telegram.");
                    resolve(false);
                    return;
                }
                resolve(true);
            });
        });
    };

    const requestUserToEnableLocation = () => {
        WebApp.showConfirm(
            "Доступ к геопозиции отключён. Чтобы включить его:\n\n" +
            "1️⃣ Откройте профиль бота (@sskuds_bot)\n" +
            "2️⃣ Нажмите на его название в верхней части экрана\n" +
            "3️⃣ Включите доступ к геопозиции",
            (confirmed) => {
                if (confirmed) {
                    WebApp.openTelegramLink("https://t.me/sskuds_bot");
                }
            }
        );
    };

    const getCurrentLocation = async () => {
        try {
            if (!await checkLocationAccess()) {
                requestUserToEnableLocation();
                return null;
            }
            return new Promise((resolve) => {
                WebApp.LocationManager.getLocation((locationData) => {
                    if (!locationData) {
                        handleLocationError('Невозможно получить локацию');
                        resolve(null);
                        return;
                    }
                    const locationString = `${locationData.latitude},${locationData.longitude}`;
                    resolve(locationString);
                });
            });
        } catch (error) {
            handleLocationError(error);
            return null;
        }
    };

    const scanQR = async () => {
        try {
            // Initialize the location manager if not already inited.
            if (!WebApp.LocationManager.isInited) {
                await new Promise((resolve) => {
                    WebApp.LocationManager.init(() => {
                        if (!WebApp.LocationManager.isInited) {
                            WebApp.showAlert("Невозможно получить локацию");
                            resolve(false);
                            return;
                        }
                        resolve(true);
                    });
                });
            }

            // Get location data.
            const locationData = await getCurrentLocation();
            if (!locationData) {
                return;
            }
            setLocation(locationData);
            // Show QR scanner popup.
            WebApp.showScanQrPopup(
                { text: 'Сканировать код' },
                async (qrData) => {
                    try {
                        // Close the QR scanner popup.
                        WebApp.closeScanQrPopup();

                        if (!qrData) {
                            WebApp.showAlert('Ошибка сканирования');
                            return;
                        }

                        // Prepare the scan data.
                        const scanData = {
                            qrData,
                            location: locationData,
                            userId: user._id,
                        };
                        console.log('Scan successful:', scanData);

                        // Send the data to your server.
                        const response = await fetchWithApiFallback(apiUrl, '/qr/scan', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(scanData),
                        });

                        const result = await response.json();
                        if (!response.ok) {
                            throw new Error(`Ошибка: ${result.message || "не определена"}`);
                        }

                        // Check the response from your endpoint.
                        if (result.status === 'success') {
                            setLoginSuccess(true);
                        } else if (result.status === 'device_not_found') {
                            // Save the scanned data for later use in registration.
                            setQrData(qrData);
                            // setShowRegistration(true);
                            WebApp.showAlert('Рабочая станция не зарегистрирована');
                        } else if (result.status === 'access_denied') {
                            WebApp.showAlert('Отказано в доступе, у вас нет прав на данное рабочее место. Обратитесь к администратору.');
                            WebApp.closeScanQrPopup();
                        } else if (result.status === 'nfcMode_always' || result.status === 'nfcMode_geoMismatch') {
                            WebApp.closeScanQrPopup();
                            if (result.sessionId) {
                                setSessionId(result.sessionId);
                                setDeviceId(result.deviceId);
                            }
                            setShowNfc(true);
                        } else if (result.status === 'location_mismatch') {
                            WebApp.showAlert('Не совпадает геолокация АРМ. Отказано в доступе.');
                            WebApp.closeScanQrPopup();
                        } else {
                            WebApp.showAlert('Неожиданный ответ от сервера');
                        }
                    } catch (error) {
                        console.error('Error processing QR code:', error);
                        WebApp.showAlert(error.message);
                        WebApp.closeScanQrPopup();
                    }
                }
            );
        } catch (error) {
            console.error('Error in scanQR:', error);
            WebApp.showAlert('Ошибка сканирования');
            WebApp.closeScanQrPopup();
        }
    };

    const showUserOTP = () => {
        setOtpDisplayMode('user');
    };

    const showAdminOTP = () => {
        setOtpDisplayMode('admin');
    };

    // Callback from RegisterDevice when registration is successful.
    const handleRegistrationSuccess = () => {
        setShowRegistration(false);
        setLoginSuccess(true);
    };

    const handleBack = () => {
        setLoginSuccess(false);
        setShowRegistration(false);
        setOtpDisplayMode(null);
        setShowNfc(false);
        setShowLPAdmin(false);
        setShowSettings(false);
        setQrData(null);
    };

    return (
        <div className="container">
            {isLocked && user?.appLock?.enabled && (
                <LockScreen user={user} onUnlock={unlockApp} />
            )}
            {loginSuccess ? (
                <div className="flex justify-center items-center min-h-screen p-2">
                    <div className="w-full max-w-md bg-gray-800 text-white p-6 rounded-lg text-center">
                        <h2 className="text-xl font-semibold mb-4">Успешно авторизован. Нажмите "Войти" на экране Windows</h2>
                        <button
                            onClick={handleBack}
                            className="w-full bg-blue-500 py-2 rounded-md hover:bg-blue-600 transition-colors"
                        >
                            ◄ Назад
                        </button>
                    </div>
                </div>
            ) : showRegistration ? (
                <div className="registration-container">
                    <button className="back-button" onClick={handleBack}>
                        ◄ Назад
                    </button>
                    <RegisterDevice
                        qrData={qrData}
                        location={location}
                        apiUrl={apiUrl}
                        onRegistrationSuccess={handleRegistrationSuccess}
                    />
                </div>
            ) : otpDisplayMode ? (
                <div className="otp-container">
                    <button className="back-button" onClick={handleBack}>
                        ◄ Назад
                    </button>
                    <OTPDisplay isAdmin={otpDisplayMode === 'admin'} />
                </div>
            ) : showNfc ? (
                <div className="nfc-container">
                    <button className="back-button" onClick={() => setShowNfc(false)}>
                        ◄ Назад
                    </button>
                    <NfcButton
                        onClick={handleNFC}
                    />
                </div>
            ) : showLPAdmin ? (
                <div className="lp-admin-container">
                    <button className="back-button" onClick={handleBack}>
                        ◄ Назад
                    </button>
                    <LPAdminPanel user={user} apiUrl={apiUrl} onBack={() => setShowLPAdmin(false)} />
                </div>
            ) : showSettings ? (
                <Settings onBack={() => setShowSettings(false)} />
            ) : (
                <div className="button-container">
                    <button className="action-button qr-button" onClick={scanQR}>
                        <svg className="icon" viewBox="0 0 24 24">
                            <path
                                fill="currentColor"
                                d="M2 2h8v8H2V2zm2 2v4h4V4H4zm-2 12h8v8H2v-8zm2 2v4h4v-4H4zm12-14h8v8h-8V2zm2 2v4h4V4h-4zm-2 12h8v8h-8v-8zm2 2v4h4v-4h-4z"
                            />
                        </svg>
                        <span>Сканирование QR</span>
                    </button>
                    {user && (
                        <button className="action-button otp-button" onClick={showUserOTP}>
                            <svg className="icon" viewBox="0 0 24 24">
                                <path
                                    fill="currentColor"
                                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                                />
                            </svg>
                            <span>Показать Мастер-пароль</span>
                        </button>
                    )}
                    {isGlobalAdmin && (
                        <button className="action-button admin-otp-button" onClick={showAdminOTP}>
                            <svg className="icon" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12c5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                            </svg>
                            <span>Мастер-пароль Админа</span>
                        </button>
                    )}
                    <button className="action-button otp-button" onClick={handleSupportClick}>
                        <svg className="icon" viewBox="0 0 24 24">
                            <path
                                fill="currentColor"
                                d="M20 2H4C2.9 2 2 2.9 2 4v14c0 1.1.9 2 2 2h4v4l4-4h8c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM4 16V4h16v12H11.17L8 19.17V16H4z"
                            />
                        </svg>
                        <span>Техподдержка</span>
                    </button>
                    {isGlobalAdmin && (
                        <button className="action-button admin-button" onClick={goToAdmin}>
                            <svg className="icon" viewBox="0 0 24 24">
                                <path
                                    fill="currentColor"
                                    d="M3 3h18v18H3V3zm2 2v14h14V5H5zm7 2c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5 2.24-5 5-5zm0 2c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                                />
                            </svg>
                            <span>{user?.isSuperAdmin ? 'Суперадмин' : 'Вход в админку'}</span>
                        </button>
                    )}
                    <button className="action-button otp-button" onClick={handleNFC}>
                        <svg className="icon" viewBox="0 0 24 24">
                            <path
                                fill="currentColor"
                                d="M20.5,2h-17C2.67,2,2,2.67,2,3.5v17C2,21.33,2.67,22,3.5,22h17c0.83,0,1.5-0.67,1.5-1.5v-17C22,2.67,21.33,2,20.5,2z
                                    M10,19H6V5h4V19z M18,19h-4V5h4V19z"
                            />
                        </svg>
                        <span>NFC</span>
                    </button>
                    <button className="action-button otp-button" onClick={() => setShowSettings(true)}>
                        <svg className="icon" viewBox="0 0 24 24">
                            <path
                                fill="currentColor"
                                d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"
                            />
                        </svg>
                        <span>Настройки</span>
                    </button>
                    {isLPAdmin && (
                        <button className="action-button admin-button" onClick={() => setShowLPAdmin(true)}>
                            <svg className="icon" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                            </svg>
                            <span>Управление</span>
                        </button>
                    )}
                </div>
            )}
            {isPrivacyShieldVisible && (
                <div className="miniapp-privacy-shield" aria-hidden="true">
                    <span>Экран скрыт для защиты данных</span>
                </div>
            )}
        </div>
    );
};

export default Home;